import React, { useEffect, useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Types moved from graphexecution library
interface Conversation {
    id: string;
    name: string;
}

interface NodeInterfaceProps {
    integration: {
        callIntegration: <T>(params: { url: string; method: string; params?: any }) => Promise<{ data: T }>;
    };
    nodeShape: {
        nodeInPorts: Array<{ name: string }>;
        nodeOutPorts: Array<{ name: string }>;
    };
    updateShape: (shape: { inPorts: Array<{ name: string }>; outPorts: Array<{ name: string }> }) => void;
    nodeState: Record<string, any>;
    setNodeState: (key: string, value: any) => void;
}

export const SlackChannelSelector: React.FC<NodeInterfaceProps> = props => {
    const [selectedConversationId, setSelectedConversationId] = useState<string | null>(props.nodeState.selectedConversationId || null);
    const [conversationFromPort, setConversationFromPort] = useState<boolean>(props.nodeState.conversationFromPort || false);
    const [conversationList, setConversationList] = useState<Conversation[]>([]);

    // Update node state when local state changes
    useEffect(() => {
        props.setNodeState('selectedConversationId', selectedConversationId);
    }, [selectedConversationId]);

    useEffect(() => {
        props.setNodeState('conversationFromPort', conversationFromPort);
    }, [conversationFromPort]);

    useEffect(() => {
        let aborted = false;
        async function performEffect() {
            function getChannels(cursor?: string) {
                return props.integration.callIntegration<{
                    channels: Conversation[];
                    response_metadata?: { next_cursor?: string };
                }>({
                    url: 'https://slack.com/api/users.conversations',
                    method: 'get',
                    params: {
                        ...(cursor ? { cursor } : {}),
                        exclude_archived: true,
                        limit: 50,
                        types: ['public_channel', 'private_channel'],
                    },
                });
            }

            async function pageThroughChannels(cursor?: string): Promise<Conversation[]> {
                const result = await getChannels(cursor);
                if (result.data) {
                    if (result.data.response_metadata?.next_cursor) {
                        return [...result.data.channels, ...(await pageThroughChannels(result.data.response_metadata.next_cursor))];
                    } else {
                        return result.data.channels;
                    }
                } else {
                    return [];
                }
            }

            try {
                const channels: Conversation[] = await pageThroughChannels();
                if (aborted) {
                    return;
                }
                setConversationList(channels);
            } catch (error) {
                console.error('Failed to load Slack channels:', error);
            }
        }
        performEffect();
        return () => {
            aborted = true;
        };
    }, []);

    useEffect(() => {
        if (conversationList.length > 0 && !conversationList.find(item => item.id === selectedConversationId)) {
            setSelectedConversationId(conversationList[0].id);
        }
    }, [conversationList, selectedConversationId]);

    const renderChannelSelect = () => {
        if (conversationFromPort) {
            return null;
        }

        return (
            <div>
                <Label>Channel</Label>
                <Select
                    onValueChange={value => {
                        const foundConversation = conversationList.find(item => item.id == value);
                        if (foundConversation) {
                            setSelectedConversationId(value);
                        }
                    }}
                    value={selectedConversationId ?? conversationList[0]?.id ?? ''}
                >
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {conversationList.map(conversation => (
                            <SelectItem key={conversation.id} value={conversation.id}>
                                {conversation.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        );
    };

    return (
        <div className="flex flex-col p-1">
            <div className="flex items-center space-x-2">
                <Checkbox checked={conversationFromPort} onCheckedChange={checked => setConversationFromPort(!!checked)} />
                <Label>Channel from Port</Label>
            </div>
            {renderChannelSelect()}
        </div>
    );
};
